import React, { useState, useEffect, useRef } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react'
import InteractiveQuestion from '../questions/InteractiveQuestion'

interface QuestionData {
  id: string
  question: string
  type: string
  options: string[]
  correctAnswer: number
  explanation: string
  hint?: string
}

interface InteractiveVTGraphProps {
  title?: string
  description?: string
  interactiveQuestions?: QuestionData[]
  onQuestionComplete?: () => void
  lessonId?: string
}

/**
 * Interactive V-T Graph - Mô phỏng đồ thị vận tốc - thời gian
 * Cho phép thay đổi gia tốc (hệ số góc) và quan sát sự thay đổi
 */
const InteractiveVTGraph: React.FC<InteractiveVTGraphProps> = ({
  title = "Đồ thị v-t trong chuyển động biến đổi đều",
  description = "Kéo trực tiếp trên đồ thị để thay đổi góc nghiêng (gia tốc) hoặc vận tốc ban đầu v₀",
  interactiveQuestions = [],
  onQuestionComplete,
  lessonId = 'current-lesson'
}) => {
  // Animation states
  const [isPlaying, setIsPlaying] = useState(false)
  const [time, setTime] = useState(0)
  const [showSettings, setShowSettings] = useState(true)  // Hiển thị luôn để dễ sử dụng

  // Physics parameters
  const [acceleration, setAcceleration] = useState(2) // m/s²
  const [initialVelocity, setInitialVelocity] = useState(5) // m/s
  const [showVectors, setShowVectors] = useState(true)
  const [showAngle, setShowAngle] = useState(true) // Hiển thị góc alpha

  // Interactive dragging states
  const [isDragging, setIsDragging] = useState(false)
  const [dragMode, setDragMode] = useState<'angle' | 'velocity' | null>(null)

  // Canvas ref
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()

  // Constants
  const maxTime = 10 // seconds
  const minVelocity = -10 // m/s for display (cho phép âm)
  const maxVelocity = 25 // m/s for display

  // Calculate current velocity: v = v0 + at
  const getCurrentVelocity = (t: number) => {
    return initialVelocity + acceleration * t
  }

  // Draw the graph
  const drawGraph = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // GIẢI PHÁP TRIỆT ĐỂ: Scale gấp đôi devicePixelRatio để có độ phân giải cực cao
    const baseDpr = window.devicePixelRatio || 1
    const superSamplingFactor = 2 // Tăng gấp đôi nữa để cực kỳ sắc nét
    const dpr = baseDpr * superSamplingFactor

    // Set display size (css pixels)
    const displayWidth = 1000
    const displayHeight = 600

    // Set actual size in memory (scaled to account for extra pixel density)
    const width = displayWidth * dpr
    const height = displayHeight * dpr

    // Set canvas buffer size
    canvas.width = width
    canvas.height = height

    // Normalize coordinate system to use css pixels
    ctx.scale(dpr, dpr)

    // Now work with display coordinates
    const workWidth = displayWidth
    const workHeight = displayHeight

    // Clear canvas với nền trắng tinh khiết
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, workWidth, workHeight)

    // Define graph area
    const margin = 70
    const graphWidth = workWidth - 2 * margin
    const graphHeight = workHeight - 2 * margin
    const originX = margin
    const originY = workHeight - margin
    const vRange = maxVelocity - minVelocity  // Khai báo một lần duy nhất
    const yZero = originY - ((0 - minVelocity) / vRange) * graphHeight // vị trí v=0

    // Enable maximum quality rendering
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Better text rendering
    ctx.textRendering = 'optimizeLegibility' as any

    // Draw axes với màu đen hoàn toàn để tăng contrast
    ctx.strokeStyle = '#000000'
    ctx.lineWidth = 3.5
    ctx.lineCap = 'square'
    ctx.beginPath()
    // X-axis (time) đi qua (0,0)
    ctx.moveTo(originX, yZero)
    ctx.lineTo(originX + graphWidth, yZero)
    // Y-axis (velocity)
    ctx.moveTo(originX, originY)
    ctx.lineTo(originX, originY - graphHeight)
    ctx.stroke()

    // Vẽ mũi tên cho trục t (ngang, bên phải)
    const arrowSizeT = 14
    const arrowEndT = originX + graphWidth
    ctx.fillStyle = '#000000'
    ctx.beginPath()
    ctx.moveTo(arrowEndT, yZero)
    ctx.lineTo(arrowEndT - arrowSizeT, yZero - arrowSizeT / 2)
    ctx.lineTo(arrowEndT - arrowSizeT, yZero + arrowSizeT / 2)
    ctx.closePath()
    ctx.fill()

    // Vẽ mũi tên cho trục v (dọc, phía trên)
    const arrowSizeV = 14
    const arrowEndV = originY - graphHeight
    ctx.beginPath()
    ctx.moveTo(originX, arrowEndV)
    ctx.lineTo(originX - arrowSizeV / 2, arrowEndV + arrowSizeV)
    ctx.lineTo(originX + arrowSizeV / 2, arrowEndV + arrowSizeV)
    ctx.closePath()
    ctx.fill()

    // Draw axis labels với font CỰC LỚN cho mobile
    ctx.fillStyle = '#000000'
    ctx.font = 'bold 26px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'

    // Nhãn t (s) ở cuối trục bên phải
    ctx.textAlign = 'right'
    ctx.fillText('t (s)', originX + graphWidth - 5, yZero - 15)

    // Nhãn v (m/s) ở đầu trục phía trên
    ctx.textAlign = 'center'
    ctx.fillText('v (m/s)', originX + 50, originY - graphHeight - 10)

    // Draw grid and labels với màu ĐEN và font LỚN
    ctx.strokeStyle = '#d1d5db'
    ctx.lineWidth = 1.5
    ctx.fillStyle = '#000000'
    ctx.font = 'bold 22px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'

    // Time grid (vertical lines) - BỎ line tại t=0 (trùng với trục)
    for (let t = 0; t <= maxTime; t += 2) {
      if (t === 0) continue  // Bỏ grid line tại trục
      const x = originX + (t / maxTime) * graphWidth
      ctx.beginPath()
      ctx.moveTo(x, originY)
      ctx.lineTo(x, originY - graphHeight)
      ctx.stroke()
      ctx.textAlign = 'center'
      ctx.fillText(t.toString(), x, originY + 30)
    }

    // Velocity grid (horizontal lines) - Scale từ -10 đến 25, BỎ line tại v=0 (trùng với trục t)
    for (let v = minVelocity; v <= maxVelocity; v += 5) {
      if (v === 0) continue  // Bỏ grid line tại v=0 (trùng với trục t)
      const y = originY - ((v - minVelocity) / vRange) * graphHeight
      ctx.beginPath()
      ctx.moveTo(originX, y)
      ctx.lineTo(originX + graphWidth, y)
      ctx.stroke()
      ctx.textAlign = 'right'
      ctx.fillText(v.toString(), originX - 15, y + 8)
    }

    // Draw v-t line (v = v0 + at) với màu đậm hơn
    ctx.strokeStyle = '#2563eb'
    ctx.lineWidth = 5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.shadowColor = 'rgba(37, 99, 235, 0.3)'
    ctx.shadowBlur = 2
    ctx.beginPath()

    for (let t = 0; t <= maxTime; t += 0.05) {
      const v = getCurrentVelocity(t)
      const x = originX + (t / maxTime) * graphWidth
      const y = originY - ((v - minVelocity) / vRange) * graphHeight

      if (t === 0) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.stroke()

    // Reset shadow
    ctx.shadowColor = 'transparent'
    ctx.shadowBlur = 0

    // Draw current point
    const currentV = getCurrentVelocity(time)
    const currentX = originX + (time / maxTime) * graphWidth
    const currentY = originY - ((currentV - minVelocity) / vRange) * graphHeight

    // Point on graph với viền đậm
    ctx.strokeStyle = '#dc2626'
    ctx.lineWidth = 3
    ctx.fillStyle = '#ef4444'
    ctx.beginPath()
    ctx.arc(currentX, currentY, 10, 0, 2 * Math.PI)
    ctx.fill()
    ctx.stroke()

    // Add white inner border
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(currentX, currentY, 10, 0, 2 * Math.PI)
    ctx.stroke()

    // BỎ đường gạch đứt từ điểm đỏ xuống trục t - không cần thiết

    // Draw angle alpha hoặc triangle Δv/Δt (không hiển thị cả 2 cùng lúc)
    if (showAngle) {
      // VẼ GÓC ALPHA TẠI ĐIỂM V₀ (điểm xuất phát)
      const t0 = 0
      const v0 = initialVelocity

      const x0 = originX
      const y0 = originY - ((v0 - minVelocity) / vRange) * graphHeight

      // Vẽ đường ngang nét đứt từ điểm v₀
      ctx.strokeStyle = '#dc2626'
      ctx.lineWidth = 3
      ctx.setLineDash([10, 10])
      ctx.beginPath()
      ctx.moveTo(x0, y0)
      ctx.lineTo(x0 + 200, y0)  // Kéo dài sang phải
      ctx.stroke()
      ctx.setLineDash([])

      // Tính điểm trên đường v-t để vẽ góc
      const t1 = 2
      const v1 = getCurrentVelocity(t1)
      const x1 = originX + (t1 / maxTime) * graphWidth
      const y1 = originY - ((v1 - minVelocity) / vRange) * graphHeight

      // Tính góc alpha dựa trên gia tốc thực tế
      const deltaY = y0 - y1  // Chênh lệch y trên canvas (âm nếu đi lên)
      const deltaX = x1 - x0  // Chênh lệch x trên canvas
      const angleAlpha = Math.atan(Math.abs(deltaY) / deltaX)
      const arcRadius = 70

      // Vẽ cung tròn biểu diễn góc alpha
      ctx.strokeStyle = '#dc2626'
      ctx.lineWidth = 3.5
      ctx.beginPath()
      if (acceleration >= 0) {
        // Nhanh dần: góc đi lên
        ctx.arc(x0, y0, arcRadius, 0, -angleAlpha, true)
      } else {
        // Chậm dần: góc đi xuống
        ctx.arc(x0, y0, arcRadius, 0, angleAlpha, false)
      }
      ctx.stroke()

      // Vẽ label α CỰC LỚN (không hiển thị giá trị)
      ctx.fillStyle = '#dc2626'
      ctx.font = 'bold 32px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      ctx.textAlign = 'center'
      const labelAngleX = x0 + arcRadius * 0.7
      const labelAngleY = acceleration >= 0 ? y0 - arcRadius * 0.25 : y0 + arcRadius * 0.35
      ctx.fillText('α', labelAngleX, labelAngleY)

    } else if (showVectors) {
      // VẼ TAM GIÁC Δv VÀ Δt (chỉ khi không hiển thị góc)
      const t1 = Math.max(time, 2)
      const t2 = t1 + 1
      const v1 = getCurrentVelocity(t1)
      const v2 = getCurrentVelocity(t2)

      const x1 = originX + (t1 / maxTime) * graphWidth
      const y1 = originY - ((v1 - minVelocity) / vRange) * graphHeight
      const x2 = originX + (t2 / maxTime) * graphWidth
      const y2 = originY - ((v2 - minVelocity) / vRange) * graphHeight

      // Draw right triangle với màu xanh đậm
      ctx.strokeStyle = '#059669'
      ctx.lineWidth = 3.5
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.beginPath()
      ctx.moveTo(x1, y1)
      ctx.lineTo(x2, y1)
      ctx.lineTo(x2, y2)
      ctx.stroke()

      // Label Δt and Δv với font CỰC LỚN
      ctx.fillStyle = '#059669'
      ctx.font = 'bold 22px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('Δt = 1s', (x1 + x2) / 2, y1 + 32)
      ctx.textAlign = 'left'
      ctx.fillText(`Δv = ${(v2 - v1).toFixed(1)} m/s`, x2 + 12, (y1 + y2) / 2)
    }

    // BỎ các giá trị t= và v= bên phải - không cần thiết

    // Display formula ở GIỮA ĐỒ THỊ với font LỚN và màu đen
    ctx.font = 'bold 26px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    ctx.fillStyle = '#000000'
    ctx.textAlign = 'center'

    // Vị trí giữa đồ thị
    const centerX = originX + graphWidth / 2
    const topY = 50

    ctx.fillText(`v = v₀ + at`, centerX, topY)
    ctx.fillText(`v = ${initialVelocity} + ${acceleration} × t`, centerX, topY + 40)

    // BỎ vẽ icon bàn tay trên canvas - dùng custom cursor CSS thay thế
  }

  // Animation loop
  useEffect(() => {
    if (isPlaying) {
      const animate = () => {
        setTime(prevTime => {
          const newTime = prevTime + 0.05
          if (newTime >= maxTime) {
            setIsPlaying(false)
            return maxTime
          }
          return newTime
        })
        animationFrameRef.current = requestAnimationFrame(animate)
      }
      animationFrameRef.current = requestAnimationFrame(animate)
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isPlaying])

  // Redraw when parameters change
  useEffect(() => {
    drawGraph()
  }, [time, acceleration, initialVelocity, showVectors, showAngle])

  const handleReset = () => {
    setIsPlaying(false)
    setTime(0)
  }

  const handleAccelerationChange = (value: number) => {
    setAcceleration(value)
    if (time > 0) {
      setTime(0) // Reset time when changing acceleration
    }
  }

  // Interactive canvas handlers
  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return null

    const rect = canvas.getBoundingClientRect()
    let clientX, clientY

    if ('touches' in e) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }

    const dpr = (window.devicePixelRatio || 1) * 2
    const x = (clientX - rect.left) * dpr
    const y = (clientY - rect.top) * dpr

    return { x, y }
  }

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoordinates(e)
    if (!coords) return

    const canvas = canvasRef.current
    if (!canvas) return

    const dpr = (window.devicePixelRatio || 1) * 2
    const workWidth = 1000
    const workHeight = 600
    const margin = 70
    const originX = margin
    const originY = workHeight - margin

    // Chuyển đổi về tọa độ logic
    const logicX = coords.x / dpr
    const logicY = coords.y / dpr

    // Kiểm tra click gần trục y (để thay đổi v₀)
    if (logicX < originX + 50) {
      setDragMode('velocity')
      setIsDragging(true)
    } else {
      // Click vào vùng đồ thị → thay đổi góc
      setDragMode('angle')
      setIsDragging(true)
    }
  }

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return

    const coords = getCanvasCoordinates(e)
    if (!coords) return

    const canvas = canvasRef.current
    if (!canvas) return

    const dpr = (window.devicePixelRatio || 1) * 2
    const workWidth = 1000
    const workHeight = 600
    const margin = 70
    const graphWidth = workWidth - 2 * margin
    const graphHeight = workHeight - 2 * margin
    const originX = margin
    const originY = workHeight - margin
    const minVelocity = -10
    const maxVelocity = 25

    const logicX = coords.x / dpr
    const logicY = coords.y / dpr

    if (dragMode === 'velocity') {
      // Thay đổi vận tốc ban đầu dựa trên vị trí Y
      const vRange = maxVelocity - minVelocity
      const newV = minVelocity + ((originY - logicY) / graphHeight) * vRange
      const clampedV = Math.max(-10, Math.min(15, newV))
      setInitialVelocity(parseFloat(clampedV.toFixed(1)))
    } else if (dragMode === 'angle') {
      // Tính gia tốc mới dựa trên góc
      const deltaX = logicX - originX
      const deltaY = originY - logicY

      if (deltaX > 50) {
        const vRange = maxVelocity - minVelocity
        const v0Y = originY - ((initialVelocity - minVelocity) / vRange) * graphHeight
        const slope = (v0Y - logicY) / deltaX
        const newA = slope * (graphWidth / maxTime) * vRange / graphHeight
        const clampedA = Math.max(-5, Math.min(5, newA))
        setAcceleration(parseFloat(clampedA.toFixed(1)))
      }
    }
  }

  const handleCanvasMouseUp = () => {
    setIsDragging(false)
    setDragMode(null)
  }

  const handleCanvasMouseEnter = () => {
    // Cursor handled by CSS
  }

  const handleCanvasMouseLeave = () => {
    setIsDragging(false)
    setDragMode(null)
  }

  // Touch event handlers
  const handleCanvasTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    handleCanvasMouseDown(e as any)
  }

  const handleCanvasTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    handleCanvasMouseMove(e as any)
  }

  const handleCanvasTouchEnd = () => {
    handleCanvasMouseUp()
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-4">
      <style>{`
        input[type="range"].interactive-slider {
          -webkit-appearance: none;
          appearance: none;
          background: linear-gradient(to right, #93c5fd 0%, #3b82f6 50%, #1d4ed8 100%);
          border-radius: 6px;
          outline: none;
          height: 8px;
        }
        input[type="range"].interactive-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          background: #3b82f6;
          border: 3px solid white;
          border-radius: 50%;
          cursor: grab;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        input[type="range"].interactive-slider::-webkit-slider-thumb:active {
          cursor: grabbing;
          background: #1d4ed8;
          transform: scale(1.1);
        }
        input[type="range"].interactive-slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          background: #3b82f6;
          border: 3px solid white;
          border-radius: 50%;
          cursor: grab;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        input[type="range"].interactive-slider::-moz-range-thumb:active {
          cursor: grabbing;
          background: #1d4ed8;
          transform: scale(1.1);
        }
        
        .canvas-interactive {
          cursor: grab;
        }
        
        .canvas-interactive.dragging {
          cursor: grabbing;
        }
      `}</style>

      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
        <p className="text-sm text-blue-600 font-medium mt-1">💡 Kéo trực tiếp trên đồ thị để thay đổi góc α hoặc v₀</p>
      </div>

      {/* Canvas */}
      <div className="mb-4 bg-blue-50 rounded-lg p-6 flex justify-center border-2 border-blue-200">
        <canvas
          ref={canvasRef}
          style={{
            width: '1000px',
            height: '600px',
            maxWidth: '100%',
            imageRendering: 'crisp-edges',
            touchAction: 'none'
          }}
          className={`border-2 border-blue-400 rounded-lg shadow-lg transition-all hover:shadow-xl hover:border-blue-500 canvas-interactive ${isDragging ? 'dragging' : ''}`}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseEnter={handleCanvasMouseEnter}
          onMouseLeave={handleCanvasMouseLeave}
          onTouchStart={handleCanvasTouchStart}
          onTouchMove={handleCanvasTouchMove}
          onTouchEnd={handleCanvasTouchEnd}
        />
      </div>

      {/* Interactive hint */}
      <div className="mb-4 bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-300 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="text-5xl" style={{ color: '#f59e0b', textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>👆</div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-orange-900">Tương tác trực tiếp trên đồ thị!</p>
            <p className="text-xs text-orange-800 mt-1">
              <strong>Kéo trên đồ thị:</strong> Thay đổi góc α (gia tốc) •
              <strong> Kéo gần trục v:</strong> Thay đổi v₀
            </p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-4">
        {/* Play controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="btn-primary px-4 py-2 rounded-lg flex items-center gap-2"
          >
            {isPlaying ? (
              <>
                <Pause className="w-4 h-4" />
                Tạm dừng
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Chạy
              </>
            )}
          </button>
          <button
            onClick={handleReset}
            className="btn-secondary px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Đặt lại
          </button>
        </div>

        {/* Parameter controls - Hiển thị luôn */}
        {showSettings && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-5 space-y-5 shadow-sm">
            <div className="flex items-center gap-2 text-blue-900 font-semibold mb-3">
              <span className="text-2xl" style={{ filter: 'hue-rotate(40deg) saturate(2)' }}>🎚️</span>
              <span>Điều chỉnh tham số:</span>
            </div>

            {/* Acceleration slider */}
            <div>
              <label className="block text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#f59e0b" className="flex-shrink-0">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                  <circle cx="12" cy="16" r="1.5" fill="#f59e0b" />
                </svg>
                Gia tốc (a): <span className="text-blue-600 text-lg">{acceleration} m/s²</span>
              </label>
              <input
                type="range"
                min="-5"
                max="5"
                step="0.5"
                value={acceleration}
                onChange={(e) => handleAccelerationChange(parseFloat(e.target.value))}
                className="w-full interactive-slider"
              />
              <div className="flex justify-between text-xs font-medium text-gray-600 mt-2">
                <span>-5</span>
                <span>0</span>
                <span>+5</span>
              </div>
            </div>

            {/* Initial velocity slider */}
            <div>
              <label className="block text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#f59e0b" className="flex-shrink-0">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                  <circle cx="12" cy="16" r="1.5" fill="#f59e0b" />
                </svg>
                Vận tốc ban đầu (v₀): <span className="text-blue-600 text-lg">{initialVelocity} m/s</span>
              </label>
              <input
                type="range"
                min="-10"
                max="15"
                step="0.5"
                value={initialVelocity}
                onChange={(e) => setInitialVelocity(parseFloat(e.target.value))}
                className="w-full interactive-slider"
              />
              <div className="flex justify-between text-xs font-medium text-gray-600 mt-2">
                <span>-10</span>
                <span>0</span>
                <span>+15</span>
              </div>
            </div>

            {/* Bỏ checkbox - luôn hiển thị góc α */}
          </div>
        )}
      </div>

      {/* Interactive Instructions */}
      <div className="mt-4 bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">🖱️ Cách tương tác:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Kéo trên đồ thị</strong> (vùng giữa) → Thay đổi <strong>góc nghiêng</strong> (gia tốc a)</li>
          <li>• <strong>Kéo gần trục v</strong> (bên trái) → Thay đổi <strong>vận tốc ban đầu v₀</strong></li>
          <li>• Trên <strong>điện thoại</strong>: Dùng ngón tay kéo trực tiếp trên đồ thị</li>
        </ul>
      </div>

      {/* Information panel */}
      <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-2">💡 Quan sát:</h4>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• <strong>Hệ số góc</strong> của đường thẳng chính là <strong>gia tốc (a)</strong></li>
          <li>• a {'>'} 0: Đường nghiêng lên → <strong>chuyển động nhanh dần</strong></li>
          <li>• a {'<'} 0: Đường nghiêng xuống → <strong>chuyển động chậm dần</strong></li>
          <li>• a = 0: Đường nằm ngang → <strong>chuyển động đều</strong></li>
          <li>• <strong>Góc α</strong> (đỏ): Góc nghiêng của đường v-t. Góc càng lớn → đường càng dốc → gia tốc càng lớn</li>
        </ul>
      </div>

      {/* Interactive Questions */}
      {interactiveQuestions && interactiveQuestions.length > 0 && (
        <div className="mt-6 space-y-4">
          <h4 className="text-lg font-bold text-gray-800">🎯 Câu hỏi tương tác</h4>
          <p className="text-sm text-gray-600 mb-4">
            Hãy thử nghiệm với các thanh trượt ở trên, sau đó trả lời các câu hỏi dưới đây:
          </p>
          {interactiveQuestions.map((q, index) => (
            <InteractiveQuestion
              key={q.id || `question-${index}`}
              question={q.question}
              options={q.options}
              correctAnswer={q.correctAnswer}
              explanation={q.explanation}
              hint={q.hint}
              difficulty="easy"
              lessonId={lessonId}
              onAnswer={(isCorrect) => {
                if (isCorrect && onQuestionComplete) {
                  onQuestionComplete()
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default InteractiveVTGraph

